import { TourConfig } from './types';

export const SitemapTour: TourConfig = {
    id: 'sitemap-tour',
    name: 'Sitemap Tour',
    description: 'Learn how to navigate and use the Sitemap to find different areas of the factory',
    steps: [
        {
            selector: 'body',
            content: (
                <div>
                    <h3 className="text-lg font-bold mb-2">Welcome to Sitemap!</h3>
                    <p>
                        This tour will guide you through the main features of the sitemap.
                        You'll learn how to navigate the 3D factory map, search for machines,
                        and view real-time heatmaps.
                    </p>
                </div>
            ),
            mobilePosition: 'center' as const,
        },
        {
            selector: '[data-tour="machine-search"]',
            content: (
                <div>
                    <h3 className="text-lg font-bold mb-2">Machine Search</h3>
                    <p>
                        Use this search bar to quickly find and navigate to specific machines
                        in the factory. Start typing a machine ID and select from the suggestions.
                    </p>
                    <p className="mt-2 text-sm" style={{ opacity: 0.8 }}>
                        Tip: Press <kbd className="px-2 py-1 rounded" style={{ backgroundColor: 'rgba(128, 128, 128, 0.2)' }}>Ctrl+K</kbd> or{' '}
                        <kbd className="px-2 py-1 rounded" style={{ backgroundColor: 'rgba(128, 128, 128, 0.2)' }}>Cmd+K</kbd> to quickly focus the search.
                    </p>
                </div>
            ),
            position: 'bottom' as const,
            mobilePosition: 'center' as const,
        },
        {
            selector: '[data-tour="heatmap-filter"]',
            content: (
                <div>
                    <h3 className="text-lg font-bold mb-2">Heatmap Filter Options</h3>
                    <p>
                        Choose what data to display on the heatmap:
                    </p>
                    <ul className="list-disc list-inside mt-2 space-y-1">
                        <li><strong>None:</strong></li>
                        <li><strong>Order:</strong> Shows order-related metrics</li>
                        <li><strong>Inserts:</strong> Shows insert-related metrics</li>
                    </ul>
                </div>
            ),
            position: 'left' as const,
            mobilePosition: 'center' as const,
        },
        {
            selector: '[data-tour="toggle-heatmap"]',
            content: (
                <div>
                    <h3 className="text-lg font-bold mb-2">Toggle Heatmap Visibility</h3>
                    <p>
                        Click this button to show or hide the heatmap overlay on the factory floor.
                        The heatmap displays real-time power consumption data for different areas.
                    </p>
                </div>
            ),
            position: 'bottom' as const,
            mobilePosition: 'center' as const,
        },
        {
            selector: '[data-tour="refresh-heatmap"]',
            content: (
                <div>
                    <h3 className="text-lg font-bold mb-2">Refresh Heatmap</h3>
                    <p>
                        Click here to refresh the heatmap data and get the latest information
                        from the machines on the factory floor.
                    </p>
                </div>
            ),
            position: 'left' as const,
            mobilePosition: 'center' as const,
        },
        {
            selector: '[data-tour="auto-refresh-toggle"]',
            content: (
                <div>
                    <h3 className="text-lg font-bold mb-2">Auto-Refresh Toggle</h3>
                    <p>
                        Enable or disable automatic heatmap refreshing. When enabled, the heatmap
                        will automatically update at your selected interval to show the latest data.
                    </p>
                    <p className="mt-2">
                        The button will be highlighted when auto-refresh is active. A dropdown will appear
                        to select the refresh frequency (1s, 10s, 30s, 1m, 2m, 3m, or 15m).
                    </p>
                    <p className="mt-2 text-sm" style={{ opacity: 0.8 }}>
                        Note: Auto-refresh updates happen silently in the background without showing the loading indicator.
                    </p>
                </div>
            ),
            position: 'left' as const,
            mobilePosition: 'center' as const,
        },
        {
            selector: '[data-tour="heatmap-scale"]',
            content: (
                <div>
                    <h3 className="text-lg font-bold mb-2">Heatmap Scale Guide</h3>
                    <p>
                        This color-coded scale shows the power consumption levels across the factory.
                        The values range from 0 kW (bottom) to the maximum detected power usage (top).
                    </p>
                </div>
            ),
            position: 'left' as const,
            mobilePosition: 'center' as const,
        },
        {
            selector: '[data-tour="lock-scale"]',
            content: (
                <div>
                    <h3 className="text-lg font-bold mb-2">Lock Heatmap Scale</h3>
                    <p>
                        Use this button to lock or unlock the heatmap scaling. When locked,
                        the scale remains constant even when data updates, making it easier
                        to compare values over time.
                    </p>
                </div>
            ),
            position: 'left' as const,
            mobilePosition: 'center' as const,
        },
        {
            selector: 'body',
            content: (
                <div>
                    <h3 className="text-lg font-bold mb-2">You're All Set! 🎉</h3>
                    <p>
                        You now know the basics of navigating the Factory View. Click on any
                        machine in the 3D view to see detailed information about it.
                    </p>
                    <p className="mt-2">
                        Happy monitoring!
                    </p>
                </div>
            ),
            mobilePosition: 'center' as const,
        },
    ],
};
